/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    addressTwo: state.components.contactUsForm.fieldValues["addressTwo"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressTwoStyles {
    addressTwoTextField?: TextFieldPresentationProps;
}

export const addressTwoStyles: AddressTwoStyles = {
    addressTwoTextField: {
        cssOverrides: {
            formField: css`
                margin-bottom: 15px;
                width: 45%;
                @media (max-width: 768px) {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = addressTwoStyles;

const AddressTwo: React.FC<Props> = ({ fields, addressTwo, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [addressTwoErrorMessage, setAddressTwoErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.addressTwo = validateAddressTwo;
        if (typeof addressTwo !== "undefined") {
            validateAddressTwo();
        }        
    }, [addressTwo]);

    React.useEffect(() => {
        contactFormContext.validators.addressTwo = validateAddressTwo;
    }, [isRequired]);

    const validateAddressTwo = () => {
        const errorMessage = isRequired && !addressTwo ? (siteMessage("Field_Required", label) as string) : "";
        setAddressTwoErrorMessage(errorMessage);
        return !errorMessage;
    };

    const addressTwoChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "addressTwo", value: event.target.value });
    };


    return (
        <TextField
            {...styles.addressTwoTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={addressTwo || ""}
            error={addressTwoErrorMessage}
            minLength={1}
            maxLength={100}
            onChange={addressTwoChangeHandler}
            optional={true}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressTwo),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Address Two",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Address Two",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
